import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { KsefAdminApiService } from 'shared/services/api/ksef-admin.api.service';
import { BatchSessionDetailsDialogComponent } from '../../dialogs/batch-session-details-dialog/batch-session-details-dialog.component';

@Component({
  selector: 'app-batch-sessions-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatPaginatorModule,
    FormsModule
  ],
  templateUrl: './batch-sessions-list.component.html',
  styleUrls: ['./batch-sessions-list.component.scss']
})
export class BatchSessionsListComponent implements OnInit {
  
  displayedColumns: string[] = ['sessionRef', 'user', 'status', 'invoices', 'createdAt', 'processingTime', 'actions'];
  dataSource = new MatTableDataSource<any>([]);
  
  isLoading = false;
  statusFilter: string = '';
  emailSearch: string = '';
  
  // Paginacja
  pageSize = 20;
  pageIndex = 0;
  totalItems = 0;
  pageSizeOptions = [10, 20, 50, 100];
  
  // Stats
  stats = {
    pending: 0,
    checking: 0,
    processing: 0,
    completed: 0,
    timeout: 0,
    error: 0
  };

  // Debounce dla wyszukiwania
  private searchSubject = new Subject<string>();

  constructor(
    private ksefAdminApi: KsefAdminApiService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    // Debounce wyszukiwania po email
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      this.pageIndex = 0; // Reset na pierwszą stronę
      this.loadSessions();
    });

    this.loadSessions();
  }

  loadSessions() {
    this.isLoading = true;

    const params: any = {
      limit: this.pageSize,
      skip: this.pageIndex * this.pageSize,
      sortBy: 'createdAt',
      sortOrder: 'desc' as 'desc'
    };

    if (this.statusFilter) {
      params.status = this.statusFilter;
    }

    if (this.emailSearch.trim()) {
      params.email = this.emailSearch.trim();
    }

    this.ksefAdminApi.getBatchSessions(params).subscribe({
      next: (response) => {
        if (response.success) {
          this.dataSource.data = response.data.sessions;
          this.totalItems = response.data.pagination.total;
          this.stats = response.data.statusCounts || this.stats;
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Failed to load batch sessions:', error);
        this.isLoading = false;
      }
    });
  }

  onSearchChange() {
    this.searchSubject.next(this.emailSearch);
  }

  onStatusFilterChange() {
    this.pageIndex = 0;
    this.loadSessions();
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadSessions();
  }

  /**
   * Kliknięcie w email - wyszukaj po tym emailu
   */
  searchByEmail(email: string, event: Event) {
    event.stopPropagation();
    this.emailSearch = email;
    this.pageIndex = 0;
    this.loadSessions();
  }

  clearSearch() {
    this.emailSearch = '';
    this.pageIndex = 0;
    this.loadSessions();
  }

  refresh() {
    this.loadSessions();
  }

  openDetails(session: any) {
    this.dialog.open(BatchSessionDetailsDialogComponent, {
      width: '1400px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { sessionId: session.id }
    });
  }

  retrySession(session: any, event: Event) {
    event.stopPropagation();

    if (!confirm(`Czy na pewno chcesz retry sesję ${session.sessionRef}?`)) {
      return;
    }

    this.ksefAdminApi.retryBatchSession(session.id).subscribe({
      next: (response) => {
        if (response.success) {
          alert('Sesja została zresetowana i będzie sprawdzona ponownie.');
          this.loadSessions();
        }
      },
      error: (error) => {
        console.error('Failed to retry session:', error);
        alert('Błąd podczas retry sesji.');
      }
    });
  }

  getStatusColor(status: string): string {
    const colors: any = {
      'pending': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      'checking': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      'processing': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
      'completed': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      'timeout': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      'error': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
  }

  getStatusIcon(status: string): string {
    const icons: any = {
      'pending': 'tabler_outline:clock',
      'checking': 'tabler_outline:refresh',
      'processing': 'tabler_outline:loader',
      'completed': 'tabler_outline:check',
      'timeout': 'tabler_outline:clock-x',
      'error': 'tabler_outline:alert-circle'
    };
    return icons[status?.toLowerCase()] || 'tabler_outline:help-circle';
  }

  getStatusIconColor(status: string): string {
    const colors: any = {
      'pending': 'text-yellow-600 dark:text-yellow-400',
      'checking': 'text-blue-600 dark:text-blue-400',
      'processing': 'text-indigo-600 dark:text-indigo-400',
      'completed': 'text-green-600 dark:text-green-400',
      'timeout': 'text-orange-600 dark:text-orange-400',
      'error': 'text-red-600 dark:text-red-400'
    };
    return colors[status?.toLowerCase()] || 'text-gray-600 dark:text-gray-400';
  }

  getStatusLabel(status: string): string {
    const labels: any = {
      'pending': 'Oczekuje',
      'checking': 'Sprawdzanie',
      'processing': 'Przetwarzanie',
      'completed': 'Zakończona',
      'timeout': 'Timeout',
      'error': 'Błąd'
    };
    return labels[status] || status;
  }

  formatProcessingTime(ms: number | null): string {
    if (!ms) return '-';
    
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}min`;
  }

  canRetry(session: any): boolean {
    return ['timeout', 'error', 'processing', 'checking'].includes(session.status);
  }
}

